import React, { useState, useEffect } from 'react';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import WizardProgressBar from './components/WizardProgressBar';
import RoomStep from './components/RoomStep';
import EquipmentStep from './components/EquipmentStep';
import ServiceStep from './components/ServiceStep';
import PaymentStep from './components/PaymentStep';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../services/supabaseService';
import { emailNotificationService } from '../../services/serviceZoneService';

const STORAGE_KEY = 'plumberpro_booking_draft';
const BOOKING_HISTORY_KEY = 'plumberpro_booking_history';

const ServiceBookingWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [bookingData, setBookingData] = useState({});
  const [isCompleted, setIsCompleted] = useState(false);
  const [stepValidation, setStepValidation] = useState({});
  const [showRecoveryBanner, setShowRecoveryBanner] = useState(false);
  const [recoveredData, setRecoveredData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        const savedTime = parsed?.timestamp;
        const currentTime = Date.now();
        const hoursSinceLastSave = (currentTime - savedTime) / (1000 * 60 * 60);
        
        if (hoursSinceLastSave < 24) {
          setRecoveredData(parsed);
          setShowRecoveryBanner(true);
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (error) {
        console.error('Failed to parse saved booking data:', error);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  const saveToLocalStorage = (data, step) => {
    try {
      const dataToSave = {
        ...data,
        currentStep: step,
        timestamp: Date.now(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Failed to save booking data:', error);
    }
  };

  const clearLocalStorage = () => {
    localStorage.removeItem(STORAGE_KEY);
  };

  const saveToBookingHistory = (completedBooking) => {
    try {
      const history = localStorage.getItem(BOOKING_HISTORY_KEY);
      const bookings = history ? JSON.parse(history) : [];
      
      const updatedHistory = [...bookings, {
        ...completedBooking,
        completedAt: Date.now()
      }]?.slice(-10);
      
      localStorage.setItem(BOOKING_HISTORY_KEY, JSON.stringify(updatedHistory));
    } catch (error) {
      console.error('Failed to save booking history:', error);
    }
  };

  const handleRecoverData = () => {
    if (recoveredData) {
      setBookingData(recoveredData);
      setCurrentStep(recoveredData?.currentStep || 0);
      setShowRecoveryBanner(false);
    }
  };

  const handleDismissRecovery = () => {
    setShowRecoveryBanner(false);
    clearLocalStorage();
  };

  const steps = [
    { id: 'room', label: 'Pièce', component: RoomStep },
    { id: 'equipment', label: 'Équipement', component: EquipmentStep },
    { id: 'service', label: 'Service', component: ServiceStep },
    { id: 'payment', label: 'Paiement', component: PaymentStep },
  ];

  const handleStepUpdate = (data) => {
    const updatedData = { ...bookingData, ...data };
    setBookingData(updatedData);
    saveToLocalStorage(updatedData, currentStep);
  };

  const handleNext = (isValid = true) => {
    if (!isValid) {
      return;
    }
    
    setStepValidation((prev) => ({ ...prev, [currentStep]: true }));
    
    if (currentStep < steps?.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      saveToLocalStorage(bookingData, nextStep);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      saveToLocalStorage(bookingData, prevStep);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleComplete = async () => {
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const appointmentData = {
        reference: `RDV-${Date.now()}`,
        customerName: `${bookingData?.firstName} ${bookingData?.lastName}`,
        customerEmail: bookingData?.email || '',
        customerPhone: bookingData?.phone || '',
        serviceType: bookingData?.service?.name || 'Service',
        urgency: bookingData?.urgency?.label || 'Standard',
        status: 'planned',
        customerAddress: `${bookingData?.address}, ${bookingData?.postalCode} ${bookingData?.city}`,
        customerLatitude: 48.8566,
        customerLongitude: 2.3522,
        priceTotal: bookingData?.total || 0,
        paymentStatus: bookingData?.paymentStatus || 'succeeded',
        paymentMethod: bookingData?.paymentMethod || 'card'
      };

      const { data: appointment, error: appointmentError } = await supabase?.from('appointments')?.insert({
          reference: appointmentData?.reference,
          customer_name: appointmentData?.customerName,
          customer_email: appointmentData?.customerEmail,
          customer_phone: appointmentData?.customerPhone,
          service_type: appointmentData?.serviceType,
          urgency: appointmentData?.urgency,
          status: appointmentData?.status,
          customer_address: appointmentData?.customerAddress,
          customer_latitude: appointmentData?.customerLatitude,
          customer_longitude: appointmentData?.customerLongitude,
          price_total: appointmentData?.priceTotal,
          payment_status: appointmentData?.paymentStatus,
          payment_method: appointmentData?.paymentMethod,
          notes: `${bookingData?.room?.name} - ${bookingData?.equipment?.name}`
        })?.select()?.single();

      if (appointmentError) throw appointmentError;

      try {
        await emailNotificationService?.sendBookingNotification(
          {
            reference: appointment?.reference,
            customerName: appointment?.customer_name,
            customerEmail: appointment?.customer_email,
            serviceType: appointment?.service_type,
            customerAddress: appointment?.customer_address,
            priceTotal: appointment?.price_total
          },
          'booking_confirmed'
        );
      } catch (emailError) {
        console.error('Email notification failed:', emailError);
      }

      saveToBookingHistory({
        room: bookingData?.room,
        equipment: bookingData?.equipment,
        service: bookingData?.service,
        urgency: bookingData?.urgency
      });

      localStorage?.removeItem(STORAGE_KEY);
      setShowRecoveryBanner(false);
      setIsSubmitting(false);
      setIsCompleted(true);
    } catch (error) {
      console.error('Booking submission error:', error);
      setSubmitError(error?.message || 'Une erreur est survenue lors de la réservation');
      setIsSubmitting(false);
    }
  };

  const handleViewTracking = () => {
    navigate('/public-service-tracking');
  };

  const handleNewBooking = () => {
    setCurrentStep(0);
    setBookingData({});
    setIsCompleted(false);
    clearLocalStorage();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLogout = () => {
    navigate('/login');
  };

  const CurrentStepComponent = steps?.[currentStep]?.component;

  if (isCompleted) {
    return (
      <>
        <RoleBasedHeader userRole="customer" userName="Invité" onLogout={handleLogout} />
        <div className="main-content bg-background min-h-screen">
          <div className="max-w-3xl mx-auto px-4 lg:px-6 py-12">
            <div className="bg-card rounded-lg shadow-lg p-8 lg:p-12 text-center">
              <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="CheckCircle" size={48} color="var(--color-success)" />
              </div>

              <h1 className="text-3xl font-bold text-foreground mb-4">
                {bookingData?.service?.type === 'quote' ?'Demande de devis envoyée !' :'Réservation confirmée !'}
              </h1>

              <p className="text-lg text-muted-foreground mb-8">
                {bookingData?.service?.type === 'quote' ?'Nous avons bien reçu votre demande. Un technicien vous contactera sous 2h.' :'Votre intervention est planifiée. Vous recevrez un email de confirmation.'}
              </p>

              <div className="bg-muted/50 rounded-lg p-6 mb-8 text-left">
                <h3 className="font-semibold text-foreground mb-4">Détails de votre réservation</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Icon name="MapPin" size={20} className="text-primary mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Adresse</p>
                      <p className="text-sm text-muted-foreground">
                        {bookingData?.address}, {bookingData?.postalCode} {bookingData?.city}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Icon name="Home" size={20} className="text-primary mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Localisation</p>
                      <p className="text-sm text-muted-foreground">{bookingData?.room?.name} - {bookingData?.equipment?.name}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Icon name="Wrench" size={20} className="text-primary mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Service</p>
                      <p className="text-sm text-muted-foreground">{bookingData?.service?.name}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Icon name="Clock" size={20} className="text-primary mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Créneau</p>
                      <p className="text-sm text-muted-foreground">{bookingData?.urgency?.name}</p>
                    </div>
                  </div>

                  {bookingData?.total > 0 && (
                    <div className="flex items-start gap-3 pt-3 border-t border-border">
                      <Icon name="CreditCard" size={20} className="text-primary mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-foreground">Montant</p>
                        <p className="text-lg font-bold text-primary">{bookingData?.total?.toFixed(2)} € TTC</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="default"
                  size="lg"
                  iconName="MapPin"
                  onClick={handleViewTracking}
                >
                  Suivre mon intervention
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Plus"
                  onClick={handleNewBooking}
                >
                  Nouvelle réservation
                </Button>
              </div>

              <div className="mt-8 pt-8 border-t border-border">
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Bell" size={16} />
                  <span>Vous recevrez des notifications par email et SMS</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <RoleBasedHeader userRole="customer" userName="Invité" onLogout={handleLogout} />
      <div className="main-content bg-background min-h-screen">
        {showRecoveryBanner && (
          <div className="bg-warning/10 border-b-2 border-warning/20">
            <div className="max-w-7xl mx-auto px-4 lg:px-6 py-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Icon name="AlertCircle" size={24} color="var(--color-warning)" />
                  <div>
                    <p className="font-semibold text-foreground">
                      Réservation en cours récupérée
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Nous avons retrouvé une réservation non terminée. Souhaitez-vous la reprendre ?
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDismissRecovery}
                  >
                    Ignorer
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleRecoverData}
                  >
                    Reprendre
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <WizardProgressBar currentStep={currentStep} steps={steps} totalSteps={steps?.length} />

        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8 lg:py-12">
          <CurrentStepComponent
            data={bookingData}
            onUpdate={handleStepUpdate}
            onNext={handleNext}
            onBack={handleBack}
            onComplete={handleComplete}
          />
        </div>
      </div>
    </>
  );
};

export default ServiceBookingWizard;